// src/features/dashboard/SuperAdminDashboard.jsx
import axios from 'axios';
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  fetchAllOrganisations,
  addOrganisation,
  updateOrganisation,
  deleteOrganisation,
  fetchAllUserDirectory,
  adminAddUser,
  adminUpdateUser,
  adminDeleteUser,
  fetchAllUsers,
  updateOrganisationDefaults,
  syncOrganisationPermissions,
  bulkUpdateUserStatus, // 👈 NEW import
} from "../../services/sheetDataService";
import { SHEET_IDS } from "../../core/config";
import { StatCard } from "./superadmin/StatCard";
import { OrganisationFormInline } from "./superadmin/OrganisationFormInline";
import { OrganisationsTable } from "./superadmin/OrganisationsTable";
import { ManageAdminsForm } from "./superadmin/ManageAdminsForm";
import { SuperAdminFormInline } from "./superadmin/SuperAdminFormInline";
import { SuperAdminsTable } from "./superadmin/SuperAdminsTable";

const MASTER_SHEET_ID = SHEET_IDS.MASTER;

const SuperAdminDashboard = () => {
  const { accessToken, user } = useAuth();

  const [organisations, setOrganisations] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showOrgForm, setShowOrgForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);

  const [showSuperAdminForm, setShowSuperAdminForm] = useState(false);

  const [showManageAdminsForm, setShowManageAdminsForm] = useState(false);
  const [managingOrg, setManagingOrg] = useState(null);
  const [adminDefaults, setAdminDefaults] = useState({});
  const [existingAdmins, setExistingAdmins] = useState([]);

  // For manual sync button
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');

  const fetchData = async () => {
    if (!accessToken) return setError('Not authenticated') && setLoading(false);
    try {
      const [orgs, userDir] = await Promise.all([
        fetchAllOrganisations(accessToken),
        fetchAllUserDirectory(accessToken),
      ]);
      setOrganisations(orgs || []);
      const superAdminUsers = (userDir || []).filter(u => u.role?.toLowerCase() === 'superadmin');
      setSuperAdmins(superAdminUsers);
    } catch (err) {
      console.error(err);
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to fetch admins for a specific organisation
  const fetchAdminsForOrg = async (org) => {
    try {
      const usersSpreadsheetId = org.usersSpreadsheetId || org.spreadsheetId;
      const allUsers = await fetchAllUsers(accessToken, usersSpreadsheetId);
      const admins = allUsers.filter(u => u.role?.toLowerCase() === 'admin');
      setExistingAdmins(admins);
      if (admins.length > 0) {
        const first = admins[0];
        setAdminDefaults({
          school: first.school || '',
          province: first.province || '',
          district: first.district || '',
          circuit: first.circuit || '',
        });
      } else {
        setAdminDefaults({ school: '', province: '', district: '', circuit: '' });
      }
    } catch (err) {
      console.error('Failed to fetch admins:', err);
      setExistingAdmins([]);
      setAdminDefaults({ school: '', province: '', district: '', circuit: '' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [accessToken]);

  // ---- Organisation handlers ----
  const handleAddOrg = () => {
    setEditingOrg(null);
    setShowOrgForm(true);
  };
  const handleEditOrg = (org) => {
    setEditingOrg(org);
    setShowOrgForm(true);
  };
  const handleCancelOrgForm = () => {
    setShowOrgForm(false);
    setEditingOrg(null);
  };

  const handleSaveOrg = async (orgData) => {
    try {
      const isNew = !editingOrg;
      const { adminData, bulkStatus, ...orgPayload } = orgData; // 👈 extract bulkStatus
      delete orgPayload.adminName;
      delete orgPayload.adminSurname;
      delete orgPayload.adminEmail;
      delete orgPayload.adminGender;
      delete orgPayload.adminSchool;
      delete orgPayload.adminProvince;
      delete orgPayload.adminDistrict;
      delete orgPayload.adminCircuit;

      // ✅ DUPLICATE CHECK (additional safety net)
      const nameExists = organisations.some(o =>
        o.organisationName.toLowerCase() === orgPayload.organisationName.toLowerCase() &&
        (isNew || o.organisationId !== orgPayload.organisationId)
      );
      if (nameExists) {
        alert('An organisation with this name already exists. Please use a different name.');
        return;
      }

      // 1. Save organisation
      if (isNew) {
        await addOrganisation(accessToken, orgPayload);
        const usersSpreadsheetId = orgPayload.usersSpreadsheetId || orgPayload.spreadsheetId;
        const adminUserData = {
          name: adminData.name,
          surname: adminData.surname,
          email: adminData.email,
          role: 'admin',
          organisationId: orgPayload.organisationId,
          school: adminData.school || 'N/A',
          province: adminData.province || 'N/A',
          district: adminData.district || 'N/A',
          circuit: adminData.circuit || 'N/A',
          grade: 'N/A',
          teacher: 'N/A',
          subject: 'N/A',
          googleClassroomLink: 'N/A',
          schedule: [],
          zoomLink: 'N/A',
          zoomPassword: 'N/A',
          studentType: 'N/A',
          gender: adminData.gender || '',
          status: 'active',
        };
        await adminAddUser(accessToken, usersSpreadsheetId, adminUserData);
      } else {
        await updateOrganisation(accessToken, editingOrg.rowNumber, orgPayload);
      }

      // 2. Bulk status update (if selected)
      if (bulkStatus && bulkStatus.trim() !== '' && orgPayload.organisationId) {
        const usersSheetId = orgPayload.usersSpreadsheetId || orgPayload.spreadsheetId;
        if (usersSheetId) {
          const updatedCount = await bulkUpdateUserStatus(
            accessToken,
            usersSheetId,
            orgPayload.organisationId,
            bulkStatus
          );
          alert(`✅ ${updatedCount} user(s) status updated to "${bulkStatus}".`);
        } else {
          console.warn('No users spreadsheet ID found, skipping bulk status update.');
        }
      }

      setShowOrgForm(false);
      setEditingOrg(null);
      await fetchData();
      alert(isNew ? 'Organisation and admin created successfully!' : 'Organisation updated successfully!');
    } catch (err) {
      alert('Failed to save organisation: ' + err.message);
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`Delete organisation ${org.organisationName}?`)) return;
    try {
      await deleteOrganisation(accessToken, org.rowNumber);
      await fetchData();
    } catch (err) {
      alert('Failed to delete organisation.');
    }
  };

  // ---- Manage Admins handlers ----
  const handleManageAdmins = async (org) => {
    setManagingOrg(org);
    await fetchAdminsForOrg(org);
    setShowManageAdminsForm(true);
  };

  const handleCancelManageAdmins = () => {
    setShowManageAdminsForm(false);
    setManagingOrg(null);
    setExistingAdmins([]);
    setAdminDefaults({});
  };

  const handleSaveAdmins = async (org, admins) => {
    const usersSpreadsheetId = org.usersSpreadsheetId || org.spreadsheetId;
    for (const admin of admins) {
      const userData = {
        name: admin.name,
        surname: admin.surname,
        email: admin.email,
        role: 'admin',
        organisationId: org.organisationId,
        school: admin.school || 'N/A',
        province: admin.province || 'N/A',
        district: admin.district || 'N/A',
        circuit: admin.circuit || 'N/A',
        grade: 'N/A',
        teacher: 'N/A',
        subject: 'N/A',
        googleClassroomLink: 'N/A',
        schedule: [],
        zoomLink: 'N/A',
        zoomPassword: 'N/A',
        studentType: 'N/A',
        gender: admin.gender || '',
        status: 'active',
      };
      await adminAddUser(accessToken, usersSpreadsheetId, userData);
    }
    await fetchAdminsForOrg(org);
    alert(`${admins.length} admin(s) added successfully!`);
  };

  const handleUpdateAdmin = async (org, updatedAdmin, applyToAll = false) => {
    const usersSpreadsheetId = org.usersSpreadsheetId || org.spreadsheetId;
    const userData = {
      name: updatedAdmin.name,
      surname: updatedAdmin.surname,
      email: updatedAdmin.email,
      role: 'admin',
      organisationId: org.organisationId,
      school: updatedAdmin.school || 'N/A',
      province: updatedAdmin.province || 'N/A',
      district: updatedAdmin.district || 'N/A',
      circuit: updatedAdmin.circuit || 'N/A',
      grade: 'N/A',
      teacher: 'N/A',
      subject: 'N/A',
      googleClassroomLink: 'N/A',
      schedule: [],
      zoomLink: 'N/A',
      zoomPassword: 'N/A',
      studentType: 'N/A',
      gender: updatedAdmin.gender || '',
      status: updatedAdmin.status || 'active',
    };

    // Update the admin
    await adminUpdateUser(
      accessToken,
      usersSpreadsheetId,
      updatedAdmin.rowNumber,
      userData,
      org.attendanceSpreadsheetId || org.spreadsheetId,
      org.marksSpreadsheetId || org.spreadsheetId
    );

    // If applyToAll is true, propagate School, Province, District, Circuit to all users
    if (applyToAll) {
      try {
        await updateOrganisationDefaults(
          accessToken,
          usersSpreadsheetId,
          org.organisationId,
          {
            school: userData.school,
            province: userData.province,
            district: userData.district,
            circuit: userData.circuit,
          }
        );
        alert('Admin updated and organisation details applied to all users.');
      } catch (err) {
        console.error('Failed to apply organisation defaults:', err);
        alert('Admin updated, but failed to apply defaults to all users: ' + err.message);
      }
    } else {
      alert('Admin updated successfully!');
    }

    // Refresh the admins list
    await fetchAdminsForOrg(org);
  };

  // Delete an admin
  const handleDeleteAdmin = async (org, admin) => {
    const usersSpreadsheetId = org.usersSpreadsheetId || org.spreadsheetId;
    await adminDeleteUser(accessToken, usersSpreadsheetId, admin);
    await fetchAdminsForOrg(org);
  };

  // ---- Superadmin handlers ----
  const handleAddSuperAdmin = () => setShowSuperAdminForm(true);
  const handleCancelSuperAdminForm = () => setShowSuperAdminForm(false);
  const handleSaveSuperAdmin = async (formData) => {
    try {
      const userId = `SA_${Date.now()}`;
      const row = [
        formData.email,
        '',
        userId,
        formData.name,
        formData.surname,
        'superadmin'
      ];
      await axios.post(
        `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/UserDirectory!A:F:append`,
        { values: [row] },
        {
          params: { valueInputOption: 'USER_ENTERED' },
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      setShowSuperAdminForm(false);
      await fetchData();
      alert('Super admin added successfully!');
    } catch (err) {
      alert('Failed to add super admin: ' + err.message);
    }
  };
  const handleDeleteSuperAdmin = async (sa) => {
    if (!window.confirm(`Delete super admin ${sa.name} ${sa.surname}?`)) return;
    try {
      const range = `UserDirectory!A${sa.rowNumber}:F${sa.rowNumber}`;
      const emptyRow = [['', '', '', '', '', '']];
      await axios.put(
        `https://sheets.googleapis.com/v4/spreadsheets/${MASTER_SHEET_ID}/values/${range}`,
        { values: emptyRow },
        {
          params: { valueInputOption: 'RAW' },
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      await fetchData();
    } catch (err) {
      alert('Failed to delete super admin.');
    }
  };

  // ---- Manual Sync All Organisations ----
  const handleSyncAllPermissions = async () => {
    if (!window.confirm('This will sync spreadsheet permissions for ALL organisations based on user statuses. This may take a few moments. Proceed?')) {
      return;
    }

    setSyncing(true);
    setSyncProgress('Starting sync...');

    try {
      let totalSynced = 0;
      let totalErrors = 0;

      for (let i = 0; i < organisations.length; i++) {
        const org = organisations[i];
        setSyncProgress(`Syncing ${org.organisationName} (${i + 1}/${organisations.length})...`);

        const spreadsheetIds = [
          org.usersSpreadsheetId || org.spreadsheetId,
          org.attendanceSpreadsheetId || org.spreadsheetId,
          org.marksSpreadsheetId || org.spreadsheetId,
          org.topicsSpreadsheetId || org.spreadsheetId,
        ].filter(Boolean);

        if (spreadsheetIds.length === 0) {
          console.warn(`No spreadsheet IDs for ${org.organisationName}, skipping.`);
          continue;
        }

        try {
          await syncOrganisationPermissions(accessToken, org.organisationId, spreadsheetIds);
          totalSynced++;
        } catch (err) {
          console.error(`Failed to sync ${org.organisationName}:`, err);
          totalErrors++;
        }
      }

      setSyncProgress(`Done! ${totalSynced} organisations synced, ${totalErrors} errors.`);
      alert(`✅ Sync complete!\n\n${totalSynced} organisations synced successfully.\n${totalErrors} organisations had errors (check console).`);
    } catch (err) {
      console.error('Sync failed:', err);
      setSyncProgress('❌ Sync failed');
      alert('❌ Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
      // Clear progress after a moment
      setTimeout(() => setSyncProgress(''), 5000);
    }
  };

  if (loading) return <p className="text-center mt-10">Loading dashboard...</p>;
  if (error) return <p className="text-center mt-10 text-red-500">{error}</p>;

  // 👇 Build list of existing organisation names (lowercase) to pass to the form
  const existingOrgNames = organisations.map(o => o.organisationName.toLowerCase());

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      <h1 className="text-3xl font-bold mb-6 text-center">Super Admin Dashboard</h1>

      {/* Stats + Sync Button */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
          <StatCard title="Organisations" value={organisations.length} color="bg-blue-600" icon="🏢" />
          <StatCard title="Super Admins" value={superAdmins.length} color="bg-red-600" icon="👑" />
        </div>
      </div>

      {/* Organisations Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">Organisations</h2>
          <div className="flex flex-col items-end">
            <button
              onClick={handleSyncAllPermissions}
              disabled={syncing}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium shadow"
            >
              {syncing ? '⏳ Syncing...' : '🔒 Sync All Permissions'}
            </button>
            {syncProgress && (
              <span className="text-sm text-gray-600 mt-1">{syncProgress}</span>
            )}
          </div>
        </div>
        {showOrgForm ? (
          <OrganisationFormInline
            org={editingOrg}
            onSubmit={handleSaveOrg}
            onCancel={handleCancelOrgForm}
            existingOrganisationNames={existingOrgNames}
          />
        ) : showManageAdminsForm && managingOrg ? (
          <ManageAdminsForm
            key={managingOrg.organisationId}
            organisation={managingOrg}
            onCancel={handleCancelManageAdmins}
            onSave={handleSaveAdmins}
            onUpdateAdmin={handleUpdateAdmin}
            onDeleteAdmin={handleDeleteAdmin}
            existingAdmins={existingAdmins}
            defaultValues={adminDefaults}
          />
        ) : (
          <OrganisationsTable
            organisations={organisations}
            onEdit={handleEditOrg}
            onDelete={handleDeleteOrg}
            onManageAdmins={handleManageAdmins}
          />
        )}
      </div>

      {/* Super Admins Section */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Super Admins</h2>
        {showSuperAdminForm ? (
          <SuperAdminFormInline
            onSubmit={handleSaveSuperAdmin}
            onCancel={handleCancelSuperAdminForm}
          />
        ) : (
          <SuperAdminsTable
            superAdmins={superAdmins}
            onAdd={handleAddSuperAdmin}
            onDelete={handleDeleteSuperAdmin}
          />
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;